import React, { ErrorInfo, ReactNode } from "react";
import { COREError } from "../../COREDesignSystem/Content/COREError";
import { useLocation } from "react-router-dom";

const withCustomRouter = <T extends object>(Child: React.ComponentType<T>) => {
  return (props: T & { children?: ReactNode }) => {
    const location = useLocation();
    return <Child {...props} location={location} />;
  };
};

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

class ErrorBoundary extends React.Component<{ children: ReactNode }, State> {
  public state: State = {
    hasError: false,
    isChunkError: false,
  };

  static getDerivedStateFromError(error: Error) {
    console.error(error);

    // Update state so the next render will show the fallback UI.
    return {
      hasError: true,
      isChunkError: error.message.toLowerCase().includes("chunk"),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // You can also log the error to an error reporting service
    console.error(error, info);
  }

  render() {
    if (this.state.hasError && this.state.isChunkError) {
      window.location.reload();
      return;
    }

    if (this.state.hasError) {
      return <COREError />;
    }

    return this.props.children;
  }
}

export const ErrorBoundaryWithRouter = withCustomRouter(ErrorBoundary);

export const HOCErrorBoundary = <T extends object>(
  Child: React.ComponentType<T>
) =>
  function ErrorBoundaryHOC(props: T & { children?: ReactNode }) {
    return (
      <ErrorBoundaryWithRouter>
        <Child {...props} />
      </ErrorBoundaryWithRouter>
    );
  };
